import React, { useState, useCallback, useMemo } from 'react';
import { isNil, get, noop } from 'lodash-es';
import {
  getAppliedRadius,
  getInventoryListTrackingData,
  getStaticFacet,
} from 'site-modules/shared/utils/inventory/srp-utils';
import {
  getAllFacetsBySelectedValues,
  getFacetsBySelectedValues,
} from 'site-modules/shared/utils/inventory/search-filter';
import { isMultiMMInitial } from 'site-modules/shared/utils/inventory/is-multi-make-model-initial';
import {
  getCurrentPaymentType,
  getSelectedPaymentType,
} from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import { MAKE, MODEL } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { RADIUS_FACET, RADIUS_FACET_EXTENDED } from 'site-modules/shared/constants/inventory/static-facets';
import { VisitorModel } from 'client/data/models/visitor';

export const withInventoryDataAdapter = WrappedComponent => {
  function InventoryDataAdapter(props) {
    const {
      zipCode,
      visitorLocation,
      isMobile,
      inventoryData: inventoryDataProp,
      selectedFilters,
      setModelValue,
      creativeId,
      onRendered,
      onUpdateFilters = noop,
    } = props;

    const [inventoryData, setInventoryData] = useState(inventoryDataProp);
    const [isLoading, setIsLoading] = useState(false);
    const prevSelectedFilters = usePrevious(selectedFilters);

    if (!isNil(inventoryDataProp) && inventoryData !== inventoryDataProp) {
      setInventoryData(inventoryDataProp);
      setIsLoading(false);
    }

    if (prevSelectedFilters !== selectedFilters && isLoading === false) {
      setIsLoading(true);
    }

    const facets = get(inventoryData, 'facets', []);

    const [isMultiMM, setIsMultiMM] = useState(isMultiMMInitial(selectedFilters));
    const handleSetIsMultiMM = useCallback(isMulti => {
      setIsMultiMM(isMulti);
    }, []);

    const isExpandedRadiusDueToLowResults = !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false);
    const attrRadius = get(inventoryData, 'attributes.radius');
    const isExpandedRadiusDueToLowResultsSort = isExpandedRadiusDueToLowResults && attrRadius === 6000;

    const radius = useMemo(
      () => getAppliedRadius({ inventoryData, visitorLocation, selectedFacets: selectedFilters }),
      [inventoryData, selectedFilters, visitorLocation]
    );

    const listTrackingData = useMemo(
      () =>
        getInventoryListTrackingData({
          radius,
          inventoryData,
          selectedFacets: selectedFilters,
        }),
      [inventoryData, radius, selectedFilters]
    );

    const {
      totalPages,
      pageSize,
      pageNumber,
      totalNumber: inventoryCount,
      maxAllowedPageNumber,
    } = get(inventoryData, 'inventories', {});
    const inventories = get(inventoryData, 'inventories.results', []);
    // The reason we don’t show “national” radius is related to inventory sorting speed.
    // When there is no make or model selected, it is too slow to search through inventory for 6000 radius.
    const extendedFacets = useMemo(() => {
      const shouldBeRadiusFacetExtended = !!selectedFilters?.[MAKE] && !!selectedFilters?.[MODEL];
      const radiusFacetConfig = shouldBeRadiusFacetExtended ? RADIUS_FACET_EXTENDED : RADIUS_FACET;
      const radiusFacet = getStaticFacet(radiusFacetConfig, radius.toString());
      return [...facets, radiusFacet];
    }, [facets, radius, selectedFilters]);

    const filteringByFacets = useMemo(
      () =>
        isNil(selectedFilters)
          ? []
          : getFacetsBySelectedValues(getAllFacetsBySelectedValues(extendedFacets, selectedFilters)),
      [extendedFacets, selectedFilters]
    );

    const attributes = get(inventoryData, 'attributes', {});
    const paymentType = getCurrentPaymentType(selectedFilters, get(attributes, 'paymentType'));
    const selectedPaymentType = getSelectedPaymentType(selectedFilters);

    const cardKeyPrefix = inventoryData?.hash;

    const handleUpdateFilters = useCallback(
      filters => {
        setIsLoading(true);
        onUpdateFilters(filters);
      },
      [onUpdateFilters]
    );

    const handleUpdateFiltersFailed = useCallback(() => {
      setIsLoading(false);
    }, []);

    const handleUpdateZipCode = useCallback(
      newZipCode => {
        setIsLoading(true);
        return setModelValue('location', VisitorModel, { zipCode: newZipCode });
      },
      [setModelValue]
    );

    return (
      <WrappedComponent
        {...props}
        facets={extendedFacets}
        filteringByFacets={filteringByFacets}
        paymentType={paymentType}
        selectedPaymentType={selectedPaymentType}
        inventories={inventories}
        radius={radius}
        isExpandedRadiusDueToLowResultsSort={isExpandedRadiusDueToLowResultsSort}
        attributes={attributes}
        totalPages={totalPages}
        pageSize={pageSize}
        pageNumber={pageNumber}
        inventoryCount={inventoryCount}
        maxAllowedPageNumber={maxAllowedPageNumber}
        inventoryData={inventoryData}
        onMultiMMChange={handleSetIsMultiMM}
        isMobile={isMobile}
        selectedFilters={selectedFilters}
        visitorLocation={visitorLocation}
        zipCode={zipCode}
        isMultiMM={isMultiMM}
        onUpdateFilters={handleUpdateFilters}
        onUpdateFiltersFailed={handleUpdateFiltersFailed}
        onUpdateZipCode={handleUpdateZipCode}
        isLoading={isLoading}
        cardKeyPrefix={cardKeyPrefix}
        creativeId={creativeId}
        listTrackingData={listTrackingData}
        onRendered={onRendered}
      />
    );
  }

  InventoryDataAdapter.displayName = `withInventoryDataAdapter(${WrappedComponent.displayName || WrappedComponent.name})`;

  return InventoryDataAdapter;
};
